import React from "react";
import { Radio, Tooltip } from "antd";

/**
 * CustomRadio - A reusable radio button component with optional tooltip
 *
 * @param {boolean} checked - Whether the radio is checked
 * @param {function} onChange - Callback function when radio state changes
 * @param {boolean} disabled - Whether the radio is disabled
 * @param {string} value - Value of the radio button
 * @param {string} size - Size of the radio button ('small', 'middle', 'large')
 * @param {string} tooltip - Tooltip text to display on hover
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 * @param {ReactNode} children - Content to display next to radio (for Radio.Group usage)
 * @param {ReactNode} description - Optional helper text rendered under the label.
 *                                  When set, the label is emphasized and the
 *                                  description appears as muted sub-text (title +
 *                                  subtitle layout). Radio aligns to the top.
 * @param {string} tooltipPlacement - Placement of tooltip ('top', 'bottom', 'left', 'right')
 * @param {object} tooltipProps - Additional props for tooltip component
 */
const CustomRadio = ({
  checked,
  onChange,
  disabled = false,
  value,
  size = "middle",
  tooltip,
  className = "",
  style = {},
  children,
  description,
  tooltipPlacement = "top",
  tooltipProps = {},
  ...rest
}) => {
  const radioProps = {
    checked,
    onChange,
    disabled,
    value,
    // Top-align the dot so it sits on the same line as the label (not centered
    // against the whole label+description block). `items-start` overrides
    // antd's default wrapper alignment.
    className: `${description ? "items-start" : ""} ${className}`.trim(),
    style,
    ...rest,
  };

  // Label on the same row as the dot; description on the next row, left-aligned
  // with the label (the column starts to the right of the dot).
  const content = description ? (
    <span className="flex flex-col">
      <span className="font-medium text-sm text-black-88">{children}</span>
      <span className="text-[12px] leading-snug text-black-65">{description}</span>
    </span>
  ) : (
    children
  );

  const radioElement = <Radio {...radioProps}>{content}</Radio>;

  // If tooltip is provided, wrap with Tooltip component
  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement={tooltipPlacement} {...tooltipProps}>
        {radioElement}
      </Tooltip>
    );
  }

  return radioElement;
};

/**
 * CustomRadioGroup - A reusable radio group component
 *
 * @param {array} options - Array of option objects {label, value, disabled, tooltip, description}
 * @param {any} value - Selected value
 * @param {function} onChange - Callback when selection changes
 * @param {boolean} disabled - Whether the entire group is disabled
 * @param {string} size - Size of radio buttons
 * @param {string} direction - Layout direction ('horizontal' or 'vertical')
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 */
export const CustomRadioGroup = ({
  options = [],
  value,
  onChange,
  disabled = false,
  size = "middle",
  direction = "vertical",
  className = "",
  style = {},
  ...rest
}) => {
  return (
    <Radio.Group
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`${
        direction === "horizontal" ? "flex flex-row gap-4" : "flex flex-col gap-2"
      } ${className}`}
      style={style}
      {...rest}
    >
      {options.map((option, index) => (
        <CustomRadio
          key={option.value || index}
          value={option.value}
          disabled={option.disabled || disabled}
          tooltip={option.tooltip}
          description={option.description}
          className={option.className}
          style={option.style}
        >
          {option.label}
        </CustomRadio>
      ))}
    </Radio.Group>
  );
};

export default CustomRadio;
